/**
 * 🔓 DFCL Discovery Reveal — one-shot flip + shimmer overlay.
 *
 * When a player unlocks a binder-only card for the first time, we flip
 * from the locked "???" silhouette to the full crimson/violet frame and
 * sweep a shimmer across the revealed face.
 *
 * Driven by the parent, which diffs the current binder against a
 * localStorage set of previously-seen card ids and enqueues anything
 * freshly unlocked.
 *
 * Pure CSS animations (defined in /app/frontend/src/index.css):
 *   • `.dfcl-reveal-face`    → 3D flip + warm glow pulse
 *   • `.dfcl-reveal-shimmer` → single light sweep across the art
 */

function LockedBack({ card }) {
  const r = card?.rarity_meta || {};
  return (
    <div
      className="absolute inset-0 flex flex-col items-center justify-center text-center p-4 border-2 border-dashed"
      style={{
        background:
          "repeating-linear-gradient(45deg, #0F0A08 0px, #0F0A08 10px, #141015 10px, #141015 20px)",
        borderColor: (r.frame_hex || "#f59e0b") + "88",
        transform: "rotateY(180deg)",
        backfaceVisibility: "hidden",
      }}
    >
      <div className="text-7xl opacity-60 mb-2">🔒</div>
      <div className="text-[10px] uppercase tracking-widest text-neutral-500">Locked</div>
    </div>
  );
}

export default function DiscoveryReveal({ card, onContinue }) {
  if (!card) return null;
  const r = card.rarity_meta || {};

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/95 backdrop-blur-md flex flex-col items-center justify-center p-4"
      onClick={onContinue}
      data-testid="dfcl-discovery-reveal"
    >
      <div className="text-[9px] uppercase tracking-[0.35em] text-[#FCD34D] mb-1 animate-pulse">
        New Card Discovered
      </div>
      <div className="font-display text-3xl sm:text-4xl text-white mb-5 text-center">
        {card.name}
      </div>

      <div
        className="dfcl-reveal-stage w-[80vw] max-w-sm aspect-[3/4] relative"
        onClick={(e) => e.stopPropagation()}
        data-testid="dfcl-discovery-face"
      >
        <div
          className="dfcl-reveal-face absolute inset-0 border-2 overflow-hidden bg-[#0F0A08]"
          style={{ borderColor: r.frame_hex || "#f59e0b" }}
        >
          {card.art_url ? (
            <img
              src={card.art_url}
              alt={card.name}
              className="absolute inset-0 w-full h-full object-cover"
              style={{ backfaceVisibility: "hidden" }}
            />
          ) : (
            <div
              className="absolute inset-0 flex items-center justify-center text-6xl"
              style={{ backfaceVisibility: "hidden" }}
            >
              {card.palette?.primary_emoji || "✨"}
            </div>
          )}
          {/* One-shot shimmer sweep */}
          <div className="dfcl-reveal-shimmer absolute inset-0" />
          {/* The locked back is the flipped side; we render it on the
              back face so the 3D flip starts from the silhouette. */}
          <LockedBack card={card} />
        </div>
      </div>

      <div className="mt-4 text-center">
        <div
          className="text-[10px] uppercase tracking-widest"
          style={{ color: r.frame_hex }}
        >
          {r.stars} {card.rarity}
          {card.card_number ? ` · #${card.card_number}` : ""}
        </div>
        {card.flavor && (
          <div className="mt-2 italic text-[11px] text-neutral-400 max-w-sm px-4">
            "{card.flavor}"
          </div>
        )}
      </div>

      <button
        onClick={onContinue}
        data-testid="dfcl-discovery-continue"
        className="mt-6 bg-[#FCD34D] hover:bg-[#FBBF24] text-black font-bold uppercase tracking-widest text-xs px-6 py-2 transition"
      >
        Continue
      </button>
    </div>
  );
}
